import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import {
  Settings as SettingsIcon, User, Plus, Trash2, X, ShieldCheck
} from 'lucide-react'
import { ConfirmModal } from '../components/ConfirmModal'

function UserModal({ user, onClose, onSave, isSaving }) {
  const [form, setForm] = useState(user || {
    full_name: '', email: '', designation: '', role: 'Contributor'
  })

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2>{user ? 'Edit User' : 'Create New User'}</h2>
          <button className="btn btn-ghost" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              className="form-input"
              value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="e.g. John Doe"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="form-input"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="e.g. john@company.com"
            />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Designation</label>
              <input
                className="form-input"
                value={form.designation}
                onChange={e => setForm({ ...form, designation: e.target.value })}
                placeholder="e.g. IT Manager"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
              >
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="Contributor">Contributor</option>
                <option value="Auditor">Auditor</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!form.full_name || !form.email || isSaving}
            onClick={() => onSave(form)}
          >
            {isSaving ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Settings({ toast, user }) {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const isAdmin = user?.role === 'Admin'

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <ShieldCheck size={48} color="var(--danger)" style={{ marginBottom: 20 }} />
          <h2>Access Denied</h2>
          <p>You do not have permission to access System Settings. Please contact your administrator.</p>
          <a href="/" className="btn btn-primary" style={{ marginTop: 12 }}>← Back to Dashboard</a>
        </div>
      </div>
    )
  }

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers()
  })

  const createMut = useMutation({
    mutationFn: (data) => api.request('/users', { method: 'POST', body: { ...data, role: data.role === 'Contributor' ? 'Employee' : data.role } }),
    onSuccess: () => { qc.invalidateQueries(['users']); setShowModal(false); toast('User created successfully', 'success') },
    onError: e => toast(e.message, 'error')
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.request(`/users/${id}`, { method: 'PUT', body: { ...data, role: data.role === 'Contributor' ? 'Employee' : data.role } }),
    onSuccess: () => { qc.invalidateQueries(['users']); setEditing(null); toast('User updated', 'success') },
    onError: e => toast(e.message, 'error')
  })

  const deleteMut = useMutation({
    mutationFn: (id) => api.request(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries(['users']); setConfirmDelete(null); toast('User deleted', 'success') },
    onError: e => toast(e.message, 'error')
  })

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">System Settings</h1>
          <p className="page-subtitle">Manage organization users and application preferences</p>
        </div>
      </div>

      <div style={{ background: 'var(--bg-elevated)', padding: 24, borderRadius: 16, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 10, borderRadius: 10 }}>
              <User size={20} />
            </div>
            <h3 style={{ margin: 0 }}>User Management</h3>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Add User
          </button>
        </div>

        <div className="table-wrapper" style={{ maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ border: 'none' }}>
            <thead>
              <tr>
                <th>Name & Role</th>
                <th>Designation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={3}><div className="empty-state">Loading users...</div></td></tr>}
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.full_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 600 }}>{u.role}</div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{u.email}</div>
                  </td>
                  <td>{u.designation}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(u)}><SettingsIcon size={14} /></button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDelete(u)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <UserModal
          onClose={() => setShowModal(false)}
          onSave={data => createMut.mutate(data)}
          isSaving={createMut.isPending}
        />
      )}

      {editing && (
        <UserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSave={data => updateMut.mutate({ id: editing.id, data })}
          isSaving={updateMut.isPending}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteMut.mutate(confirmDelete.id)}
        title="Delete User?"
        message={`Are you sure you want to delete "${confirmDelete?.full_name}"? This action cannot be undone.`}
      />
    </div>
  )
}
